// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import React, { useEffect, useState } from "react";
import { Icon, Segment, Message } from "semantic-ui-react";
import { animateScroll } from "react-scroll";
import { withTranslation } from "react-i18next";
import _ from "lodash";

import { ChatRoom, SendMessageRequest } from "amazon-ivs-chat-messaging";

import ChatForm from "./ChatForm";
import MessagesService from "../services/messages";

const ChatBox = (props) => {
  const [liveId] = useState(props.liveId);
  const [chatId] = useState(props.chatId);
  const [username, setUserName] = useState(props.username);
  const [moderator] = useState(JSON.parse(props.isOwner));
  const [readOnly, setReadonly] = useState(false);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [chatRoom, setChatRoom] = useState([]);
  const [chatConected, setChatConected] = useState(false);

  const service = new MessagesService();

  // Fetches a chat token
  const tokenProvider = async () => {
    const data = {
      chatId: chatId,
      username: username,
      isModerator: moderator,
    };

    var token;

    await service
      .createToken(liveId, data)
      .then((response) => {
        token = {
          token: response.data.token,
          sessionExpirationTime: new Date(response.data.sessionExpirationTime),
          tokenExpirationTime: new Date(response.data.tokenExpirationTime),
        };
      })
      .catch((err) => {
        console.log(`Ops! ocorreu um erro: ${err}`);
      });

    return token;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const connectRoom = () => {
    if (username === "null" || _.isEmpty(username) === true) {
      console.log("Connect with a read only user");
      setUserName('ReadUser');
      setReadonly(true);
      return;
    }
    // Instantiate a chat room
    const room = new ChatRoom({
      regionOrUrl: "us-east-1",
      tokenProvider: () => tokenProvider(),
    });
    setChatRoom(room);
    // Connect to the chat room
    room.connect();
  };

  useEffect(() => {
    console.log(username);
  },[username])

  useEffect(() => {
    // If chat room listeners are not available, do not continue
    if (!chatRoom.addListener) {
      connectRoom();
      return;
    }

    const unsubscribeOnConnected = chatRoom.addListener("connect", () => {
      // Connected to the chat room.
      sendMessage(t("connected_message"), "join");
      setChatConected(true);
    });

    const unsubscribeOnDisconnected = chatRoom.addListener(
      "disconnect",
      (reason) => {
        // Disconnected from the chat room.
        console.log(reason);
      }
    );

    const unsubscribeOnUserDisconnect = chatRoom.addListener(
      "userDisconnect",
      (disconnectUserEvent) => {
        /* Example event payload:
         * {
         *   id: "AYk6xKitV4On",
         *   userId": "R1BLTDN84zEO",
         *   reason": "Spam",
         *   sendTime": new Date("2022-10-11T12:56:41.113Z"),
         *   requestId": "b379050a-2324-497b-9604-575cb5a9c5cd",
         *   attributes": { UserId: "R1BLTDN84zEO", Reason: "Spam" }
         * }
         */
        renderDisconnect(disconnectUserEvent.reason);
      }
    );

    const unsubscribeOnConnecting = chatRoom.addListener("connecting", () => {
      // Connecting to the chat room.
    });

    const unsubscribeOnMessageReceived = chatRoom.addListener(
      "message",
      (message) => {
        // Received a message
        const messageType = message.attributes?.message_type || "message";
        switch (messageType) {
          case "join":
            setUsers((prevState) => {
              return [...prevState, message.sender.attributes.username];
            });
            break;
          default:
            handleMessage(message);
            break;
        }
      }
    );

    return () => {
      unsubscribeOnConnected();
      unsubscribeOnDisconnected();
      unsubscribeOnUserDisconnect();
      unsubscribeOnConnecting();
      unsubscribeOnMessageReceived();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatRoom, connectRoom]);

  useEffect(() => {
    const scrollToBottom = () => {
      animateScroll.scrollToBottom({
        containerId: "chat-messages",
      });
    };
    scrollToBottom();
  });

  useEffect(() => {
    let interval = setInterval(() => {
      if (_.size(users) > 0) {
        handleJoinMessage();
        setUsers([]);
      } else {
        console.log(`Users: ${_.size(users)}`);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [users]);

  // Handlers
  const handleError = (data) => {
    const username = "";
    const userId = "";
    const avatar = "";
    const message = `Error ${data.errorCode}: ${data.errorMessage}`;
    const messageId = "";
    const timestamp = `${Date.now()}`;

    const newMessage = {
      type: "ERROR",
      timestamp,
      username,
      userId,
      avatar,
      message,
      messageId,
    };

    setMessages((prevState) => {
      return [...prevState, newMessage];
    });
  };

  const handleMessage = (data) => {
    const username = data.sender.attributes.username;
    const moderator = JSON.parse(
      data.sender.attributes.moderator.toLowerCase()
    );
    const userId = data.sender.userId;
    const avatar = data.sender.attributes.avatar;
    const message = data.content;
    const messageId = data.id;
    const timestamp = data.sendTime;

    const newMessage = {
      type: "MESSAGE",
      timestamp,
      username,
      userId,
      avatar,
      message,
      messageId,
      moderator,
    };

    setMessages((prevState) => {
      return [...prevState, newMessage];
    });
  };

  const handleJoinMessage = () => {
    const lastUser = _.last(users);
    if( lastUser !== "ReadUser"){
      const totalUsers = _.size(users);
      let message = "";
      if (totalUsers === 1) {
        message = `${lastUser} ${t("user_connected")}`;
      } else {
        message = `${lastUser} & ${totalUsers - 1} ${t("users_connected")}`;
      }

      const newMessage = {
        type: "JOIN",
        timestamp: Date.now(),
        message,
      };

      setMessages((prevState) => {
        return [...prevState, newMessage];
      });
    }
  };

  const sendMessage = async (message, type = "message") => {
    const attributes = {
      liveId: liveId,
      message_type: type,
    };
    const content = `${message.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}`;
    const request = new SendMessageRequest(content, attributes);
    try {
      await chatRoom.sendMessage(request);
    } catch (error) {
      handleError(error);
    }
  };

  // Renderers
  const renderErrorMessage = (errorMessage) => {
    return (
      <div className="error-line" key={errorMessage.timestamp}>
        <p>{errorMessage.message}</p>
      </div>
    );
  };

  const renderMessage = (message) => {
    let user = (
      <>
        {_.startCase(message.username)}&nbsp;&nbsp;&nbsp;
        {message.moderator ? <Icon color="green" name="certificate" /> : ""}
      </>
    );
    return (
      <Message
        size="small"
        header={user}
        content={message.message}
        key={message.timestamp}
      />
    );
  };

  const renderJoinMessage = (message) => {
    return (
      <Message
        size="small"
        header={t("connected_message")}
        content={message.message}
        key={message.timestamp}
      />
    );
  };

  const renderMessages = () => {
    return messages.map((message) => {
      switch (message.type) {
        case "ERROR":
          const errorMessage = renderErrorMessage(message);
          return errorMessage;
        case "MESSAGE":
          const textMessage = renderMessage(message);
          return textMessage;
        case "JOIN":
          const joinMessage = renderJoinMessage(message);
          return joinMessage;
        default:
          console.info("Received unsupported message:", message);
          return <></>;
      }
    });
  };

  const renderDisconnect = (reason) => {
    const error = {
      type: "ERROR",
      timestamp: `${Date.now()}`,
      username: "",
      userId: "",
      avatar: "",
      message: `Connection closed. Reason: ${reason}`,
    };
    setMessages((prevState) => {
      return [...prevState, error];
    });
  };

  const { t } = props;

  return (
    <>
      <Segment.Group size="mini">
        <Segment className="list-comments" id="chat-messages">
          {renderMessages()}
        </Segment>
        <Segment className="form-comment">
          <ChatForm
            addComment={sendMessage}
            placeholder={
              chatConected
                ? t("chat_connect_placeholder")
                : t("chat_disconnect_placeholder")
            }
            readOnly={readOnly}
            username={username}
            popUpFunctionName={props.popUpFunctionName}
          />
        </Segment>
      </Segment.Group>
    </>
  );
};

export default withTranslation()(ChatBox);
